import { useState } from "react";
import { useParams } from "react-router-dom";
import data from "../../data/news.json";
import { useTranslation } from "react-i18next";
import Welcome from "./details/Welcome";
import NotFound from "../NotFound";
import SEO from "../common/SEO";
import ScrollToTopLink from "../ScrollToTopLink";
import YouTubePlayer from "../YouYubePlayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faShareNodes,
  faCopy,
  faCheck,
  faFilePdf,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const NewsDetails = ({ formatDate, renderBody }) => {
  const { id } = useParams();
  const newsItem = data.find((obj) => obj.id === id);
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!newsItem) {
    return <NotFound />;
  }

  const isEnglish = (i18n.language || "it").startsWith("en");
  const lang = isEnglish ? "en" : "it";
  const title = lang === "en" ? newsItem.title_en : newsItem.title_it;
  const body = lang === "en" ? newsItem.body_en : newsItem.body_it;
  const snippet = body
    ? body.split("\n")[0].substring(0, 160)
    : `${title} - Nuova Orchestra da Camera Ferruccio Busoni`;

  // Estimate reading time (~200 words per minute)
  const wordCount = body ? body.split(/\s+/).length : 50;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  // Other news items for bottom recommendation
  const otherNews = data.filter((item) => item.id !== id).slice(0, 2);

  const articleSchema = {
    "@type": "NewsArticle",
    headline: title,
    datePublished: newsItem.date,
    dateModified: newsItem.date,
    image: newsItem.img
      ? newsItem.img.startsWith("http")
        ? newsItem.img
        : `https://www.orchestrabusoni.it${newsItem.img}`
      : undefined,
    description: snippet,
    mainEntityOfPage: `https://www.orchestrabusoni.it/news/${newsItem.id}`,
    author: {
      "@type": "Organization",
      name: "Nuova Orchestra da Camera Ferruccio Busoni",
      url: "https://www.orchestrabusoni.it",
    },
    publisher: {
      "@type": "Organization",
      name: "Nuova Orchestra da Camera Ferruccio Busoni",
      url: "https://www.orchestrabusoni.it",
      logo: {
        "@type": "ImageObject",
        url: "https://www.orchestrabusoni.it/logo.png",
      },
    },
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: isEnglish ? "News" : "Notizie", url: "/news" },
    { name: title, url: `/news/${newsItem.id}` },
  ];

  const handleCopyLink = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(title);

  return (
    <>
      <SEO
        title={title}
        description={snippet}
        image={newsItem.img}
        type="article"
        schema={articleSchema}
        breadcrumbs={breadcrumbs}
      />

      {/* Top Hero Header with Background, Title & Meta */}
      <Welcome
        date={newsItem.date}
        formatDate={formatDate}
        title={title}
        img={newsItem.img}
        readingTime={readingTime}
        isEnglish={isEnglish}
      />

      {/* Dedicated Editorial Article Layout (Shifted Down below Header) */}
      <article className="news-article-page section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-9">
              {/* Top Navigation & Share Group */}
              <div className="article-top-bar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <ScrollToTopLink to="/news" className="article-back-link">
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  <span>{isEnglish ? "All News" : "Tutte le notizie"}</span>
                </ScrollToTopLink>

                {/* Social Share Group */}
                <div className="article-share-group">
                  <span className="share-label mr-2">
                    <FontAwesomeIcon icon={faShareNodes} className="mr-1" />
                    {isEnglish ? "Share:" : "Condividi:"}
                  </span>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-fb"
                    title="Condividi su Facebook"
                    aria-label="Facebook"
                  >
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?text=${shareTitle}%20${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-btn share-wa"
                    title="Condividi su WhatsApp"
                    aria-label="WhatsApp"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`share-btn share-copy ${copied ? "copied" : ""}`}
                    title={isEnglish ? "Copy link" : "Copia link"}
                    aria-label="Copia link"
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  </button>
                  {copied && (
                    <span className="copied-toast">
                      {isEnglish ? "Copied!" : "Copiato!"}
                    </span>
                  )}
                </div>
              </div>

              {/* Featured Image (Editorial Frame) */}
              {newsItem.img && newsItem.img_in_details !== "n" && (
                <figure className="article-featured-figure mb-4">
                  <img
                    src={newsItem.img}
                    alt={title}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    className="article-featured-img img-fluid"
                  />
                  {newsItem.img_by && (
                    <figcaption className="article-img-caption">
                      {newsItem.img_by}
                    </figcaption>
                  )}
                </figure>
              )}

              {/* Article Content */}
              {body && (
                <div className="article-body-content">
                  {renderBody(body)}
                </div>
              )}

              {/* Document Download Card (Press Release PDF / File) */}
              {newsItem.download_button_url && (
                <div className="article-download-card my-4 p-4">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="download-icon-circle">
                        <FontAwesomeIcon icon={faFilePdf} />
                      </div>
                      <div>
                        <h5 className="download-title mb-1">
                          {isEnglish
                            ? "Download Press Material"
                            : "Scarica Comunicato Stampa"}
                        </h5>
                        <p className="download-desc mb-0">
                          {isEnglish
                            ? "Official document & high-resolution attachment"
                            : "Documento ufficiale e allegati completi"}
                        </p>
                      </div>
                    </div>
                    <a
                      href={newsItem.download_button_url}
                      download={title}
                      className="btn btn-download-article"
                    >
                      <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                      {t("concerts.download_button", "Scarica")}
                    </a>
                  </div>
                </div>
              )}

              {/* External Press Link Button */}
              {newsItem.button_url && (
                <div className="article-action-banner my-4 text-center">
                  <a
                    href={newsItem.button_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-article-external"
                  >
                    <span>{t("concerts.info_button", "Approfondisci")}</span>
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-2" />
                  </a>
                </div>
              )}

              {/* Embedded Video (Responsive Theater Frame) */}
              {newsItem.video_id && (
                <div className="article-video-wrapper my-5">
                  <h4 className="video-section-title mb-3">
                    {isEnglish ? "Video Feature" : "Contenuto Video"}
                  </h4>
                  <div className="article-video-container">
                    <YouTubePlayer video_id={newsItem.video_id} />
                  </div>
                </div>
              )}

              {/* Article Bottom Navigation & Other Stories */}
              <hr className="article-divider my-5" />

              <div className="article-bottom-section">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                  <h3 className="other-news-title m-0">
                    {isEnglish ? "Other Recent News" : "Altre Notizie Recenti"}
                  </h3>
                  <ScrollToTopLink to="/news" className="btn btn-outline-all-news">
                    {isEnglish ? "View all news →" : "Tutte le notizie →"}
                  </ScrollToTopLink>
                </div>

                <div className="row g-4">
                  {otherNews.map((item) => {
                    const otherTitle =
                      lang === "en" ? item.title_en : item.title_it;
                    return (
                      <div className="col-md-6" key={item.id}>
                        <ScrollToTopLink
                          to={`/news/${item.id}`}
                          className="other-news-card d-block text-decoration-none"
                        >
                          <div className="other-news-card-inner">
                            {item.img && (
                              <div className="other-news-img-wrap">
                                <img
                                  src={item.img}
                                  alt={otherTitle}
                                  className="other-news-img"
                                  loading="lazy"
                                />
                              </div>
                            )}
                            <div className="other-news-content p-3">
                              <span className="other-news-date">
                                {formatDate(item.date)}
                              </span>
                              <h5 className="other-news-heading mt-1 mb-0">
                                {otherTitle}
                              </h5>
                            </div>
                          </div>
                        </ScrollToTopLink>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default NewsDetails;
